import { parse as parseConfig } from "../../services/helpers/config";
import {
    bindTrustedActivate,
    buildSlotUrl,
    decodePacket,
    encodePacket,
    getPreviewMode,
    isTrustedActionEvent,
    openTrustedUrl,
    sendSlotBeacon
} from "../../services/helpers/slot-codec";
import { attachCanvasBadge, mountSlotShadow } from "../../services/helpers/slot-shadow";
import { updateScroll } from "../../services/live";
import ModalBlocked from "../modal/modal-blocked";
import Toast from "../modal/toast";

const boundBannerHosts = new WeakSet();

export default class Banner {
    static colors = {
        white: "rgb(255, 255, 255)",
        red: "rgb(255, 245, 247)",
        green: "rgb(244, 254, 247)",
        yellow: "rgb(253, 252, 241)",
        blue: "rgb(245, 252, 255)"
    }

    static popup = null

    static initPopup() {
        const element = document.getElementById("lead");

        if (!element) {
            return null;
        }

        const form = element.querySelector("form");
        const info = element.querySelector("[data-info-text]");
        const popup = ModalBlocked.get(element);
        const formMeta = form && form.dataset.pForm ? decodePacket(form.dataset.pForm) : null;
        const csrfToken = (formMeta && formMeta.tk) || "";

        form.addEventListener("submit", (event) => {
            event.preventDefault();
            if (!event.isTrusted) {
                return;
            }

            const body = new FormData(form);
            const email = (body.get("email") || "").toString().trim();
            const telegram = (body.get("telegram") || "").toString().trim();
            const phoneNumber = (body.get("phoneNumber") || "").toString().trim();

            if (email === "" && telegram === "" && phoneNumber === "") {
                info.textContent = "Пожалуйста, заполните одно из полей: почта, телеграм, номер телефона. Нам же нужно как-то с Вами связаться";
                info.classList.add("color_error");

                return;
            }

            const packet = encodePacket({
                op: "l",
                i: form.dataset.bid || "0",
                tk: csrfToken,
                pv: getPreviewMode() !== "" ? 1 : 0,
                nm: (body.get("name") || "").toString(),
                em: email,
                tg: telegram,
                ph: phoneNumber,
                tx: (body.get("text") || "").toString()
            });

            fetch(buildSlotUrl({ op: "l", i: form.dataset.bid || "0" }), {
                method: "POST",
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ p: packet })
            })
            .then(() => {
                popup.close();
                Toast.open(form.dataset.outro);
            });
        });

        return { popup, form, element };
    }

    constructor(container) {
        if (!container || boundBannerHosts.has(container)) {
            return null;
        }
        boundBannerHosts.add(container);

        this.showTime = null;
        this.observer = null;
        this.bannerData = null;
        this.container = container;
        this.wrap = container.closest("[data-p-wrap], [data-remove-with-banner]");
        if (this.wrap) {
            this.wrap.removeAttribute("data-p-wrap");
            this.wrap.removeAttribute("data-remove-with-banner");
        }
        this.config = this.resolveConfig();
        this.container.removeAttribute("data-p-slot");
        this.container.removeAttribute("data-config-item");
        this.shadowHandle = mountSlotShadow(this.container, { active: false });
        this.root = this.shadowHandle ? this.shadowHandle.root : this.container;
        this.banner = this.root.querySelector(".pl-card-box, .info-block");
        this.nodes = this.initNodes();

        if (!Banner.popup) {
            Banner.popup = Banner.initPopup();
        }

        if (this.banner && this.loading) {
            this.request();
        }
    }

    resolveConfig() {
        if (this.container.dataset.pSlot) {
            const decoded = decodePacket(this.container.dataset.pSlot);
            if (decoded && typeof decoded === "object") {
                return {
                    timeout: typeof decoded.t === "number" ? decoded.t : 0,
                    position: decoded.p || "post",
                    tags: Array.isArray(decoded.g) ? decoded.g : []
                };
            }
        }

        return parseConfig(this.container.dataset.configItem || "");
    }

    get loading() {
        return this.banner.classList.contains("loading");
    }

    set loading(value) {
        this.banner.classList.toggle("loading", value);
    }

    set data(value) {
        this.bannerData = Object.assign({
            "area-placeholder": "Пожелания, предложения"
        }, value);
        this.showTime = (new Date).getTime();

        this.banner.removeAttribute("href");
        this.banner.removeAttribute("target");
        this.banner.style.background = Banner.colors[value.background] || Banner.colors.white;
        if (this.nodes.header) {
            this.nodes.header.textContent = value.title;
        }
        if (this.nodes.text) {
            this.nodes.text.textContent = value.text;
        }
        if (this.nodes.button) {
            this.nodes.button.textContent = value.button;
        }
        if (this.nodes.img && value.image) {
            const imgPath = value.image;
            if (/^https?:\/\//.test(imgPath) || imgPath.indexOf("/img/") === 0) {
                this.nodes.img.src = imgPath;
            } else {
                const domain = (window.mediaDomainUrl || "").replace(/\/+$/, "");
                this.nodes.img.src = `${domain}/${imgPath.replace(/^\/+/, "")}`;
            }
        }
    }

    initNodes() {
        if (!this.banner) {
            return {};
        }

        return {
            main: this.banner.querySelector(".pl-card-box__main, .info-block__content-right"),
            header: this.banner.querySelector(".pl-card-box__title, .info-block__header"),
            text: this.banner.querySelector(".pl-card-box__summary, .info-block__text"),
            button: this.banner.querySelector(".pl-card-box__action, .info-block__btn"),
            img: this.banner.querySelector(".pl-card-box__media img, .info-block__figure img"),
            close: this.banner.querySelector("[data-p-dismiss], .info-block__close [data-close-btn]")
        };
    }

    initListener() {
        if ("IntersectionObserver" in window) {
            const options = {
                root: null,
                rootMargin: "0px",
                threshold: 0.5
            };
            const callback = (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        this.observer.unobserve(entry.target);
                        this.view();
                    }
                });
            };
            this.observer = new IntersectionObserver(callback, options);
            this.observer.observe(this.container);
        }

        if (this.nodes.close) {
            bindTrustedActivate(this.nodes.close, (e) => {
                if (!isTrustedActionEvent(e)) {
                    return;
                }
                e.preventDefault();
                e.stopPropagation();
                this.close();
            }, true);
        }

        bindTrustedActivate(this.banner, (e) => {
            if (!isTrustedActionEvent(e)) {
                e.preventDefault();

                return;
            }

            if (this.nodes.close && this.nodes.close.contains(e.target)) {
                return;
            }

            if (e.target && e.target.closest && e.target.closest("[data-p-badge]")) {
                return;
            }

            this.click();

            if (this.bannerData.hasLead) {
                e.preventDefault();
                this.openLead();
            } else if (this.bannerData.buttonUrl) {
                openTrustedUrl(e, this.bannerData.buttonUrl);
            }
        });

        if (this.config.position === "header") {
            updateScroll();
        }
    }

    remove() {
        if (this.shadowHandle) {
            this.shadowHandle.destroy();
        }
        if (this.observer) {
            this.observer.disconnect();
        }
        const removable = this.wrap || this.container.closest("[data-p-wrap], [data-remove-with-banner]");

        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }

        if (removable && removable.parentNode) {
            removable.parentNode.removeChild(removable);
        }
    }

    close() {
        if (this.config.position === "header") {
            document.body.classList.remove("has-header-info");
            const sheetHeader = document.querySelector("header.sheet__header");
            if (sheetHeader) {
                sheetHeader.classList.remove("sheet-header-info", "sheet-header--info");
            }
            document.body.dispatchEvent(new CustomEvent("sidebar.update"));
        }

        this.remove();

        if (this.config.position === "header") {
            updateScroll();
        }
    }

    normalizeResponse(raw) {
        if (!raw) {
            return null;
        }

        return {
            id: raw.i,
            title: raw.h || "",
            text: raw.x || "",
            image: raw.m || "",
            background: raw.g || "white",
            button: raw.b || "",
            buttonUrl: raw.u || "",
            hasLead: Boolean(raw.l),
            intro: raw.in || "",
            outro: raw.ou || "",
            erid: raw.er || "",
            ord: raw.or || "",
            positions: Array.isArray(raw.p) ? raw.p : []
        };
    }

    request() {
        const callback = (bannerObj) => {
            this.loading = false;

            if (bannerObj && bannerObj.positions.includes(this.config.position)) {
                const wrap = this.wrap || this.container.closest("[data-p-wrap], [data-remove-with-banner]");
                if (wrap && wrap.style.display === "none") {
                    wrap.style.display = "";
                }
                if (this.shadowHandle) {
                    this.shadowHandle.setActive(true);
                }
                this.data = bannerObj;
                attachCanvasBadge(this.banner, {
                    id: bannerObj.id,
                    url: bannerObj.buttonUrl,
                    title: bannerObj.title,
                    ord: bannerObj.ord,
                    erid: bannerObj.erid,
                    shadowHandle: this.shadowHandle
                });
                this.initListener();
            } else {
                this.close();
            }
        };

        const pv = getPreviewMode();
        const url = buildSlotUrl({
            op: "b",
            p: this.config.position || "post",
            t: Array.isArray(this.config.tags) ? this.config.tags : [],
            pv: pv || ""
        });

        window.setTimeout(() => {
            fetch(url, {
                method: "GET",
                credentials: "same-origin"
            })
            .then(response => response.json())
            .then(json => {
                const unpacked = json && json.p ? decodePacket(json.p) : null;
                const mapped = unpacked && unpacked.d ? this.normalizeResponse(unpacked.d) : null;
                callback(mapped);
            })
            .catch(() => {
                this.close();
            });
        }, this.config.timeout || 0);
    }

    view() {
        if (!this.bannerData) {
            return;
        }

        sendSlotBeacon({
            op: "v",
            i: this.bannerData.id || 0,
            p: this.config.position || "post",
            s: "__infoSlug" in window ? window.__infoSlug : ""
        });
    }

    click() {
        if (!this.bannerData) {
            return;
        }

        const time = (new Date).getTime();
        const timeSpent = Math.max(1, time - (this.showTime || time));

        sendSlotBeacon({
            op: "c",
            i: this.bannerData.id || 0,
            d: timeSpent,
            p: this.config.position || "post",
            s: "__infoSlug" in window ? window.__infoSlug : ""
        });
    }

    openLead() {
        if (!Banner.popup) {
            return;
        }

        for (const key of [ "intro", "area-placeholder" ]) {
            if (key in this.bannerData && this.bannerData[key]) {
                Banner.popup.element.querySelectorAll(`[data-key="${ key }"]`)
                    .forEach(item => item.textContent = this.bannerData[key]);
            }
        }

        Banner.popup.form.dataset.bid = this.bannerData.id;
        Banner.popup.form.dataset.outro = this.bannerData.outro || "Спасибо! Мы с Вами свяжемся";
        Banner.popup.popup.open();
    }
}
